import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Trophy, Target, Star, TrendingUp, Bell } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [comps, setComps] = useState([]);
  const [remindersOn, setRemindersOn] = useState(true);
  const [savingPref, setSavingPref] = useState(false);

  useEffect(() => {
    (async () => {
      const [s, c] = await Promise.all([api.get("/me/stats"), api.get("/competitions")]);
      setStats(s.data); setComps(c.data);
    })();
  }, []);

  useEffect(() => {
    if (user) setRemindersOn(user.email_reminders_enabled !== false);
  }, [user]);

  const toggleReminders = async () => {
    setSavingPref(true);
    try {
      const next = !remindersOn;
      await api.patch("/users/me/preferences", { email_reminders_enabled: next });
      setRemindersOn(next);
      refresh?.();
    } finally {
      setSavingPref(false);
    }
  };

  return (
    <div className="fade-up space-y-6">
      <div className="p-6 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[#C7F500] text-[#071426] font-heading font-bold flex items-center justify-center text-xl">
            {user?.name?.split(" ").map((w) => w[0]).join("").slice(0, 2)}
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold">{user?.name}</h1>
            <p className="text-sm text-[#AAB7C7]">{user?.email}</p>
            <p className="text-xs text-[#C7F500] uppercase tracking-wider mt-1">{user?.role?.replace("_", " ")}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid="profile-points">
          <div className="flex items-center justify-between text-xs text-[#AAB7C7] uppercase tracking-wider mb-2"><span>Season Points</span><Target className="w-4 h-4 text-[#C7F500]" /></div>
          <div className="font-heading text-3xl font-bold">{stats?.total_points ?? 0}</div>
        </div>
        <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid="profile-predictions">
          <div className="flex items-center justify-between text-xs text-[#AAB7C7] uppercase tracking-wider mb-2"><span>Predictions</span><TrendingUp className="w-4 h-4 text-[#C7F500]" /></div>
          <div className="font-heading text-3xl font-bold">{stats?.total_predictions ?? 0}</div>
        </div>
        <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid="profile-exact">
          <div className="flex items-center justify-between text-xs text-[#AAB7C7] uppercase tracking-wider mb-2"><span>Exact Scores</span><Trophy className="w-4 h-4 text-[#C7F500]" /></div>
          <div className="font-heading text-3xl font-bold">{stats?.exact_correct ?? 0}</div>
        </div>
        <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#F5B942]/30" data-testid="profile-joker-rate">
          <div className="flex items-center justify-between text-xs text-[#AAB7C7] uppercase tracking-wider mb-2"><span>Joker Hit Rate</span><Star className="w-4 h-4 text-[#F5B942]" /></div>
          <div className="font-heading text-3xl font-bold text-[#F5B942]">{stats?.joker_rate ?? 0}%</div>
        </div>
      </div>

      <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid="notification-preferences">
        <div className="flex items-center gap-2 mb-3">
          <Bell className="w-5 h-5 text-[#C7F500]" />
          <h2 className="font-heading text-lg font-semibold">Notifications</h2>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="font-medium">Thursday reminder email</div>
            <p className="text-xs text-[#AAB7C7] mt-1">A friendly nudge about 24 hours before your league's weekly lock if you still have picks to make.</p>
          </div>
          <button
            onClick={toggleReminders}
            disabled={savingPref}
            data-testid="reminders-toggle"
            aria-pressed={remindersOn}
            className={`relative shrink-0 w-12 h-7 rounded-full transition-colors disabled:opacity-50 ${remindersOn ? "bg-[#C7F500]" : "bg-[#132A45] border border-[#1F3A5C]"}`}
          >
            <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition-all ${remindersOn ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </div>
      </div>

      <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]">
        <h2 className="font-heading text-lg font-semibold mb-3">My competitions</h2>
        {comps.length === 0 ? (
          <p className="text-sm text-[#AAB7C7]">You haven't joined any competitions yet.</p>
        ) : (
          <div className="divide-y divide-[#1F3A5C]">
            {comps.map((c) => (
              <div key={c.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-[#AAB7C7]">{c.season}</div>
                </div>
                <span className="text-xs text-[#AAB7C7]">{c.member_count} members</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
